import { useEffect, useMemo, useRef, useState } from 'react';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Checkbox, InputGroup, Spinner, Switch } from '@blueprintjs/core';
import { toast } from 'sonner';
import {
  acceptBatch, aiSuggestMapping, confirmHeader, costOf, confirmMapping, discardDraft, fetchLeadSources,
  fetchTargetFields, requestPrepare, retryStage, selectSheets, suggestMapping, updateBatchDetails,
  type BatchDetail, type MappingRule, type MappingSuggestion, type Preview, type SheetRow, type VerifyKind,
} from './api';
import ImportConfirm from './ImportConfirm';
import { queryPolicies } from '../../utils/queryPolicies';
import { JobBanner, Pill, RowLedger, colLetter, fmtDay, fmtInt, fmtMoney, useConfirm, type LedgerPart } from './ui';
import { leadImportKeys } from './keys';
import { StoredOriginalPreview } from './SpreadsheetPreview';
import { FileChip, FilePreviewDialog } from './FileChip';
import { PreviewRowsDialog } from './PreviewRows';
import SourceLocationNote from './SourceLocationNote';
import ImportCostFields from './ImportCost';
import ReplaceFile from './ReplaceFile';
import NameHeaders from './NameHeaders';
import { PickList, type PickOption } from '../ui/PickList';

type StepId = 'file' | 'sheets' | 'columns' | 'review';
const STEPS: { id: StepId; label: string }[] = [
  { id: 'file', label: 'Source and date' },
  { id: 'sheets', label: 'Sheets' },
  { id: 'columns', label: 'Columns' },
  { id: 'review', label: 'Review and import' },
];

const included = (b: BatchDetail) => b.sheets.filter((s) => s.selected);
const headerUsable = (s: SheetRow) => s.header_status === 'ok' || s.header_status === 'needs_confirmation';
const mappingCurrent = (s: SheetRow) => !!s.mapping && s.mapping.signature === s.header_signature;

function stepDone(b: BatchDetail): Record<StepId, boolean> {
  const inc = included(b);
  return {
    file: !!b.lead_source && !!b.batch_date,
    sheets: inc.length > 0 && inc.every((s) => s.header_status === 'ok' || (s.header_status === 'needs_confirmation' && s.header_confirmed)),
    columns: inc.length > 0 && inc.every(mappingCurrent),
    review: b.status === 'Previewed' && !!b.preview?.can_accept,
  };
}

export default function ImportWizard({ batch, onOpenBatch }: { batch: BatchDetail; onOpenBatch: (id: number) => void }) {
  const queryClient = useQueryClient();
  const done = stepDone(batch);
  const firstOpen: StepId = batch.status === 'Preparing' || batch.status === 'Previewed' ? 'review' : (STEPS.find((s) => !done[s.id])?.id ?? 'review');
  const [step, setStep] = useState<StepId>(firstOpen);
  useEffect(() => { setStep(firstOpen); }, [batch.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (batch.status === 'Preparing') setStep('review'); }, [batch.status]);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch.id) });

  const discard = useMutation({
    mutationFn: () => discardDraft(batch.id),
    onSuccess: () => { toast.success('Draft discarded. The uploaded file is kept.'); queryClient.invalidateQueries({ queryKey: leadImportKeys.all }); onOpenBatch(0); },
    onError: (e) => toast.error((e as Error).message),
  });
  const [confirm, confirmDialog] = useConfirm();
  const askDiscard = async () => {
    if (await confirm({ title: 'Discard this draft?', body: 'Nothing is imported. The uploaded file stays stored and can still be downloaded.', action: 'Discard draft', danger: true })) discard.mutate();
  };

  if (batch.status === 'Inspecting' || (batch.status === 'Failed' && !batch.accepted_at && batch.stage_error?.stage === 'inspect')) {
    return <>{confirmDialog}<ReadingFile batch={batch} onDiscard={askDiscard} discarding={discard.isPending} onOpenBatch={onOpenBatch} /></>;
  }
  const currentIndex = STEPS.findIndex((s) => s.id === step);

  return (
    <div className="li-wizard">
      <nav className="li-stepper" aria-label="Import steps">
        <ol>
          {STEPS.map((s, i) => {
            const state = s.id === step ? 'current' : done[s.id] ? 'done' : 'todo';
            return (
              <li key={s.id} className={`li-stepper__item is-${state}`}>
                <button type="button" onClick={() => setStep(s.id)} aria-current={state === 'current' ? 'step' : undefined}>
                  <span className="li-stepper__num" aria-hidden>{state === 'done' ? '✓' : i + 1}</span>
                  <span className="li-stepper__label">{s.label}</span>
                </button>
              </li>
            );
          })}
        </ol>
        <button type="button" className="li-btn li-btn--quiet li-stepper__discard" disabled={discard.isPending}
          onClick={askDiscard}>
          Discard draft
        </button>
      </nav>

      {step === 'file' && <FileStep batch={batch} onDone={() => { invalidate(); setStep('sheets'); }} />}
      {step === 'sheets' && <SheetsStep batch={batch} onDone={() => { invalidate(); setStep('columns'); }} onOpenBatch={onOpenBatch} />}
      {step === 'columns' && <ColumnsStep batch={batch} onAllDone={() => { invalidate(); setStep('review'); }} />}
      {step === 'review' && <ReviewStep batch={batch} done={done} goTo={setStep} onOpenBatch={onOpenBatch} />}

      {currentIndex > 0 && step !== 'review' && (
        <button type="button" className="li-btn li-btn--quiet li-wizard__back" onClick={() => setStep(STEPS[currentIndex - 1].id)}>Back</button>
      )}
      {confirmDialog}
    </div>
  );
}

/* ── Reading the file / failed reading ────────────────── */

function ReadingFile({ batch, onDiscard, discarding, onOpenBatch }: { batch: BatchDetail; onDiscard: () => void; discarding: boolean; onOpenBatch: (id: number) => void }) {
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: () => retryStage(batch.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch.id) }),
    onError: (e) => toast.error((e as Error).message),
  });
  const failed = batch.status === 'Failed';
  return (
    <div className="li-reading">
      <div className="li-reading__head">
        <h2>{failed ? 'This file could not be read' : 'Reading your file'}</h2>
        <p>
          {failed
            ? 'Nothing was imported. The uploaded file is stored unchanged, so you can try again or download it.'
            : 'The import service is opening the workbook, counting rows and checking each sheet\'s header row. Sheets appear here when it finishes.'}
        </p>
      </div>
      <JobBanner job={batch.job} stageError={batch.stage_error} onRetry={failed ? () => retry.mutate() : undefined} retrying={retry.isPending} />
      <SourceLocationNote location={batch.source_location} />
      {failed && <ReplaceFile batch={batch.id} onReplaced={onOpenBatch} label="Replace the file" />}
      <div className="li-reading__actions">
        {batch.file && <FileChip batchId={batch.id} name={batch.file.original_filename} bytes={batch.file.byte_size} />}
        <button type="button" className="li-btn li-btn--quiet li-btn--danger" disabled={discarding}
          onClick={onDiscard}>Discard draft</button>
      </div>
    </div>
  );
}

/* ── Step 1: source and date ──────────────────────────── */

function FileStep({ batch, onDone }: { batch: BatchDetail; onDone: () => void }) {
  const queryClient = useQueryClient();
  const sources = useQuery({ queryKey: leadImportKeys.sources, queryFn: fetchLeadSources, ...queryPolicies.configuration });
  const [form, setForm] = useState({
    lead_source: batch.lead_source ? String(batch.lead_source) : '',
    sub_lead_source: batch.sub_lead_source ?? '',
    batch_date: batch.batch_date ?? batch.batch_date_suggestion ?? '',
  });
  // Choices save as they are made, so the stepper can move on at any time.
  const save = useMutation({
    scope: { id: `li-details-${batch.id}` },
    mutationFn: (patch: Partial<typeof form>) =>
      updateBatchDetails(batch.id, 'lead_source' in patch ? { ...patch, lead_source: patch.lead_source ? Number(patch.lead_source) : null } : patch),
    onSettled: () => queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch.id) }),
    onError: (e) => toast.error((e as Error).message),
  });
  const saved = { lead_source: batch.lead_source ? String(batch.lead_source) : '', sub_lead_source: batch.sub_lead_source ?? '', batch_date: batch.batch_date ?? '' };
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K], persist = true) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (persist && v !== saved[k]) save.mutate({ [k]: v });
  };
  // A date suggested from the file name counts as chosen; store it once.
  useEffect(() => {
    if (!batch.batch_date && form.batch_date) save.mutate({ batch_date: form.batch_date });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const flushText = () => { if (form.sub_lead_source !== saved.sub_lead_source) save.mutate({ sub_lead_source: form.sub_lead_source }); };
  const suggestion = batch.batch_date_suggestion;
  const missing = [!form.lead_source && 'the lead source', !form.batch_date && 'the batch date'].filter(Boolean);

  return (
    <section className="li-panel">
      <header className="li-panel__head">
        <h2>Where did this file come from?</h2>
        <p className="li-filerow"><FileChip batchId={batch.id} name={batch.file?.original_filename ?? batch.file_name} bytes={batch.file?.byte_size} /><span>{batch.sheets.length} sheet{batch.sheets.length === 1 ? '' : 's'}</span></p>
      </header>
      <SourceLocationNote location={batch.source_location} />
      <div className="li-form">
        <label className="li-field">
          <span className="li-field__label">Lead source</span>
          <PickList value={form.lead_source} onChange={(v) => set('lead_source', v)} fill large ariaLabel="Lead source" placeholder="Choose a source"
            options={(sources.data ?? []).map((s) => ({ value: String(s.id), label: s.source }))} />
        </label>
        <label className="li-field">
          <span className="li-field__label">Sub-source <span className="li-muted">optional</span></span>
          <InputGroup large value={form.sub_lead_source} placeholder="For example 14 day aged"
            onChange={(e) => set('sub_lead_source', e.target.value, false)} onBlur={flushText} />
        </label>
        <div className="li-field">
          <label className="li-field__label" htmlFor="li-batch-date">Batch date</label>
          <input id="li-batch-date" type="date" className="bp5-input bp5-large li-date" value={form.batch_date} onChange={(e) => set('batch_date', e.target.value)} />
          <span className="li-field__help">The day the batch was received{batch.batch_date_suggestion && form.batch_date === batch.batch_date_suggestion ? ', taken from the file name' : ''}.</span>
          {suggestion && suggestion !== form.batch_date && (
            <div className="li-suggest">
              <span>The file name suggests <b>{fmtDay(suggestion)}</b>.</span>
              <button type="button" className="li-btn li-btn--link" onClick={() => set('batch_date', suggestion)}>Use {fmtDay(suggestion)}</button>
            </div>
          )}
        </div>
        <ImportCostFields batch={batch} sourceChosen={!!form.lead_source} large />
      </div>
      <footer className="li-panel__foot">
        {missing.length > 0 && <span className="li-muted">Choose {missing.join(' and ')} to continue.</span>}
        <button type="button" className="li-btn li-btn--primary" disabled={missing.length > 0 || save.isPending} onClick={() => { flushText(); onDone(); }}>
          {save.isPending ? 'Saving…' : 'Continue'}
        </button>
      </footer>
    </section>
  );
}

/* ── Step 2: sheets ───────────────────────────────────── */

/** Why a sheet cannot be imported, naming the columns at fault. */
function headerProblem(s: SheetRow): string {
  const cols = (code: string) => (s.header_issues ?? []).filter((x) => x.code === code && x.position != null).map((x) => colLetter(x.position!));
  const list = (c: string[]) => (c.length === 1 ? `Column ${c[0]}` : `Columns ${c.slice(0, -1).join(', ')} and ${c[c.length - 1]}`);
  if (s.header_status === 'empty') return 'This sheet is empty.';
  if (s.header_status === 'data_in_row1') {
    const c = cols('data_in_row1');
    return c.length ? `${list(c)} ${c.length === 1 ? 'has' : 'have'} data in row 1 instead of a header. Add a header row above the data.` : 'Row 1 holds data instead of headers. Add a header row above the data.';
  }
  const c = cols('blank_label');
  return c.length ? `${list(c)} ${c.length === 1 ? 'has' : 'have'} data but no header in row 1.` : 'Row 1 is empty, so no column has a header.';
}

function SheetsStep({ batch, onDone, onOpenBatch }: { batch: BatchDetail; onDone: () => void; onOpenBatch: (id: number) => void }) {
  const queryClient = useQueryClient();
  // Nothing chosen yet: start with every visible sheet that has a usable header and data.
  const [selected, setSelected] = useState<Set<number>>(() => {
    const saved = batch.sheets.filter((s) => s.selected);
    const initial = saved.length ? saved : batch.sheets.filter((s) => headerUsable(s) && !s.is_hidden && s.nonempty_row_count > 1);
    return new Set(initial.map((s) => s.id));
  });
  const [confirmed, setConfirmed] = useState<Set<number>>(() => new Set(batch.sheets.filter((s) => s.header_confirmed).map((s) => s.id)));
  const [previewing, setPreviewing] = useState<number>(() => (batch.sheets.find((s) => s.selected) ?? batch.sheets[0])?.id ?? 0);
  const sheetById = useMemo(() => new Map(batch.sheets.map((s) => [s.id, s])), [batch.sheets]);
  const chosen = batch.sheets.filter((s) => selected.has(s.id));
  const rows = chosen.reduce((n, s) => n + Math.max(s.nonempty_row_count - 1, 0), 0);
  const unconfirmed = chosen.filter((s) => s.header_status === 'needs_confirmation' && !confirmed.has(s.id));

  // Each change saves right away, so the stepper and the columns step always
  // see what is switched on here. One scope keeps the writes in order.
  const save = useMutation({
    mutationKey: leadImportKeys.sheetChoice(batch.id),
    scope: { id: `li-sheets-${batch.id}` },
    mutationFn: (change: { ids: number[] } | { sheet: number; confirmed: boolean }) =>
      'ids' in change ? selectSheets(batch.id, change.ids) : confirmHeader(batch.id, change.sheet, change.confirmed),
    onSettled: () => queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch.id) }),
    onError: (e) => toast.error((e as Error).message),
  });
  useEffect(() => {
    if (!batch.sheets.some((s) => s.selected) && selected.size > 0) save.mutate({ ids: [...selected] });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (s: SheetRow) => {
    if (!headerUsable(s)) return;
    const n = new Set(selected);
    if (n.has(s.id)) n.delete(s.id); else n.add(s.id);
    setSelected(n);
    save.mutate({ ids: [...n] });
  };
  // A sheet whose headers were just named becomes usable: switch it on.
  const include = (s: SheetRow) => {
    if (selected.has(s.id)) return;
    const n = new Set(selected).add(s.id);
    setSelected(n);
    save.mutate({ ids: [...n] });
  };
  const toggleConfirmed = (s: SheetRow) => {
    const on = !confirmed.has(s.id);
    setConfirmed((cur) => { const n = new Set(cur); if (on) n.add(s.id); else n.delete(s.id); return n; });
    save.mutate({ sheet: s.id, confirmed: on });
  };
  const reason = selected.size === 0 ? 'Include at least one sheet.'
    : unconfirmed.length ? `Confirm the headers of ${unconfirmed.map((s) => s.sheet_name).join(', ')}.` : null;

  return (
    <section className="li-panel li-panel--wide">
      <header className="li-panel__head">
        <h2>Which sheets hold leads?</h2>
        <p>Sheets with data are switched on. Click a sheet to look at it.</p>
      </header>
      <div className="li-sheets">
        <div className="li-sheets__list" role="list" aria-label="Sheets in this file">
          {batch.sheets.map((s) => {
            const on = selected.has(s.id);
            const usable = headerUsable(s);
            return (
              <div key={s.id} role="listitem"
                className={`li-sheetcard${on ? ' is-included' : ''}${previewing === s.id ? ' is-previewing' : ''}${usable ? '' : ' is-blocked'}`}>
                <button type="button" className="li-sheetcard__main" onClick={() => setPreviewing(s.id)} aria-pressed={previewing === s.id}
                  aria-label={`Look at ${s.sheet_name}`}>
                  <span className="li-sheetcard__name">{s.sheet_name}</span>
                  <span className="li-sheetcard__meta">
                    {fmtInt(Math.max(s.nonempty_row_count - 1, 0))} rows, {s.column_count} columns{s.is_hidden ? ', hidden in the file' : ''}
                  </span>
                  {!usable && <span className="li-sheetcard__problem">{headerProblem(s)}</span>}
                  {usable && s.header_status === 'needs_confirmation' && <span className="li-sheetcard__warn">Row 1 has unusual labels.</span>}
                </button>
                <Switch className="li-sheetcard__switch" checked={on} disabled={!usable} large
                  onChange={() => toggle(s)} labelElement={<span className="li-sheetcard__switchlabel">{on ? 'Included' : 'Not included'}</span>}
                  aria-label={`Include ${s.sheet_name}`} alignIndicator="right" />
                {s.header_status === 'blank_labels' && (
                  <NameHeaders batch={batch.id} sheet={s} onNamed={() => include(s)} />
                )}
                {!usable && s.header_status !== 'empty' && (
                  <ReplaceFile batch={batch.id} onReplaced={onOpenBatch} label={`Replace the file to fix ${s.sheet_name}`} />
                )}
                {on && s.header_status === 'needs_confirmation' && (
                  <Checkbox className="li-sheetcard__confirm" checked={confirmed.has(s.id)}
                    onChange={() => toggleConfirmed(s)}
                    label={`Row 1 really is the header (${(s.header_issues ?? []).filter((i) => i.code === 'unusual_label' && i.position != null).map((i) => `"${s.header_cells?.[i.position!] ?? ''}"`).join(', ')})`} />
                )}
              </div>
            );
          })}
        </div>
        <div className="li-sheets__preview">
          <div className="li-sheets__previewhead">
            <span>Looking at <b>{sheetById.get(previewing)?.sheet_name}</b></span>
            {sheetById.get(previewing) && (selected.has(previewing) ? <Pill tone="success">Included</Pill> : <Pill tone="muted">Not included</Pill>)}
          </div>
          <StoredOriginalPreview batchId={batch.id} filename={batch.file?.original_filename ?? batch.file_name ?? ''}
            activeSheet={sheetById.get(previewing)?.sheet_name} showTabs={false} height={440} />
        </div>
      </div>
      <footer className="li-panel__foot">
        <span className="li-sheets__summary" aria-live="polite">
          <b>{chosen.length}</b> of {batch.sheets.length} sheets included, <b>{fmtInt(rows)}</b> rows
        </span>
        {reason && <span className="li-muted">{reason}</span>}
        <button type="button" className="li-btn li-btn--primary" disabled={!!reason || save.isPending} onClick={onDone}>
          {save.isPending ? 'Saving…' : 'Continue to columns'}
        </button>
      </footer>
    </section>
  );
}

/* ── Step 3: columns, one mapping per included sheet ──── */

// The select value of a column. Field choices are the field key itself.
const IGNORE = '__ignore';
const EXTRA = '__extra';
const PHONE = '__phone';
const NAME_FL = '__name_first_last';
const NAME_LF = '__name_last_first';
const isName = (v: string | undefined) => v === NAME_FL || v === NAME_LF;
const isField = (v: string | undefined) => !!v && ![IGNORE, EXTRA, PHONE, NAME_FL, NAME_LF].includes(v);

const choiceOf = (r: MappingRule | undefined): string => {
  switch (r?.use) {
    case 'field': return r.target ?? IGNORE;
    case 'name': return r.name_order === 'last_first' ? NAME_LF : NAME_FL;
    case 'phone': return PHONE;
    case 'extra': return EXTRA;
    default: return IGNORE;
  }
};
const ruleOf = (position: number, value: string): MappingRule => {
  if (value === PHONE) return { position, use: 'phone' };
  if (value === EXTRA) return { position, use: 'extra' };
  if (isName(value)) return { position, use: 'name', name_order: value === NAME_LF ? 'last_first' : 'first_last' };
  if (isField(value)) return { position, use: 'field', target: value };
  return { position, use: 'ignore' };
};

function ColumnsStep({ batch, onAllDone }: { batch: BatchDetail; onAllDone: () => void }) {
  const sheets = included(batch);
  const firstTodo = sheets.find((s) => !mappingCurrent(s)) ?? sheets[0];
  const [sheetId, setSheetId] = useState<number | undefined>(firstTodo?.id);
  useEffect(() => { if (!sheets.some((s) => s.id === sheetId)) setSheetId(firstTodo?.id); }, [sheets, sheetId, firstTodo]);
  const sheet = sheets.find((s) => s.id === sheetId);
  // A sheet switched on just before leaving the sheets step may still be saving.
  const savingSheets = useIsMutating({ mutationKey: leadImportKeys.sheetChoice(batch.id) }) > 0;
  if (!sheets.length || !sheet) {
    return <section className="li-panel"><p className={savingSheets ? 'li-muted' : undefined}>{savingSheets ? 'Saving your sheet choice...' : 'Include at least one sheet first.'}</p></section>;
  }
  const next = () => {
    const remaining = sheets.filter((s) => s.id !== sheet.id && !mappingCurrent(s));
    if (remaining.length) setSheetId(remaining[0].id);
    else onAllDone();
  };
  return (
    <section className="li-panel li-panel--wide">
      <header className="li-panel__head">
        <h2>Match columns to CRM fields</h2>
        <p>Every lead needs a phone. Columns you do not match are ignored; choose Keep as extra data to keep one on the lead.</p>
      </header>
      {sheets.length > 1 && (
        <div className="li-sheettabs" role="tablist" aria-label="Included sheets">
          {sheets.map((s) => (
            <button key={s.id} type="button" role="tab" aria-selected={s.id === sheet.id}
              className={`li-sheettab${s.id === sheet.id ? ' is-active' : ''}`} onClick={() => setSheetId(s.id)}>
              <span className={`li-sheettab__dot${mappingCurrent(s) ? ' is-done' : ''}`} aria-hidden />
              {s.sheet_name}
              <span className="li-sr">{mappingCurrent(s) ? ' (mapped)' : ' (needs mapping)'}</span>
            </button>
          ))}
        </div>
      )}
      <SheetMapper key={sheet.id} batch={batch} sheet={sheet} onSaved={next} />
    </section>
  );
}

type AiNote = { value: string; confidence: number | null; reason: string | null; changed: boolean };

function SheetMapper({ batch, sheet, onSaved }: { batch: BatchDetail; sheet: SheetRow; onSaved: () => void }) {
  const queryClient = useQueryClient();
  const suggestion = useQuery({
    queryKey: [...leadImportKeys.batch(batch.id), 'suggest', sheet.id, batch.lead_source],
    queryFn: () => suggestMapping(batch.id, sheet.id),
    ...queryPolicies.recordDetail,
  });
  const targets = useQuery({ queryKey: leadImportKeys.targets, queryFn: fetchTargetFields, ...queryPolicies.metadata });
  const [choice, setChoice] = useState<Record<number, string>>({});
  const [phoneOrder, setPhoneOrder] = useState<number[]>([]);
  // Columns the operator set by hand; AI suggestions never change them.
  const [touched, setTouched] = useState<Set<number>>(new Set());
  const [origin, setOrigin] = useState('alias');
  const [profile, setProfile] = useState<number | null>(null);
  const [profileName, setProfileName] = useState('');
  const [aiInfo, setAiInfo] = useState<{ id: number; model: string; notes: Record<number, AiNote> } | null>(null);
  const [show, setShow] = useState<'all' | 'ignored' | 'ai'>('all');
  const s = suggestion.data;

  const apply = (rules: MappingRule[], phones: number[], from: string) => {
    if (!s) return;
    const next: Record<number, string> = {};
    s.headers.forEach((h) => { next[h.position] = choiceOf(rules.find((x) => x.position === h.position)); });
    setChoice(next);
    setPhoneOrder([...phones, ...s.headers.map((h) => h.position)].filter((p, i, all) => next[p] === PHONE && all.indexOf(p) === i));
    setOrigin(from);
  };
  useEffect(() => {
    if (!s) return;
    if (sheet.mapping && mappingCurrent(sheet)) {
      apply(sheet.mapping.rules, sheet.mapping.phone_priority, sheet.mapping.origin ?? 'profile');
      setProfile(sheet.mapping.profile);
      setProfileName(sheet.mapping.profile_name);
    } else if (s.exact_profile) {
      apply(s.exact_profile.rules, s.exact_profile.phone_priority, 'profile');
      setProfile(s.exact_profile.profile);
      setProfileName(s.exact_profile.name);
    } else {
      apply(s.suggested_rules, s.suggested_phone_priority, 'alias');
    }
  }, [s]); // eslint-disable-line react-hooks/exhaustive-deps

  const ai = useMutation({
    mutationFn: () => aiSuggestMapping(batch.id, sheet.id),
    // The AI decides every column from the headers, example values and the
    // rule-based checks. Its decision replaces any column the operator has
    // not set by hand; the server kept the rule-based one where the AI's
    // answer was invalid (origin rules), so those are not marked AI.
    onSuccess: (r) => {
      const next = { ...choice };
      const notes: Record<number, AiNote> = {};
      const changed: number[] = [];
      r.rules.forEach((x) => {
        if (x.origin !== 'ai') return;
        const value = choiceOf(x);
        const change = !touched.has(x.position) && (next[x.position] ?? IGNORE) !== value;
        notes[x.position] = { value, confidence: x.confidence ?? null, reason: x.reason ?? null, changed: change };
        if (change) { next[x.position] = value; changed.push(x.position); }
      });
      setAiInfo({ id: r.suggestion, model: r.model, notes });
      if (changed.length === 0) { toast.info('AI agrees with the current choices.'); return; }
      setChoice(next);
      setPhoneOrder([...phoneOrder, ...r.phone_priority, ...Object.keys(next).map(Number)].filter((p, i, all) => next[p] === PHONE && all.indexOf(p) === i));
      setOrigin('ai');
      const names = changed.map((p) => `"${s?.headers.find((h) => h.position === p)?.header || `Column ${p + 1}`}"`);
      toast.success(`AI changed ${changed.length} column${changed.length === 1 ? '' : 's'}`, {
        description: `${names.slice(0, 4).join(', ')}${names.length > 4 ? ` and ${names.length - 4} more` : ''}. They are marked AI below; check them before saving.`,
        action: { label: 'Show them', onClick: () => setShow('ai') },
      });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const fieldOptions = useMemo(() => (targets.data ?? []).filter((t) => !t.is_phone).sort((a, b) => a.ui_label.localeCompare(b.ui_label)), [targets.data]);
  const targetOptions = useMemo<PickOption[]>(() => [
    { value: IGNORE, label: 'Ignore' },
    { value: EXTRA, label: 'Keep as extra data' },
    { value: PHONE, label: 'Phone number' },
    { value: NAME_FL, label: 'Full name, first name first', group: "Person's full name, split into first and last name" },
    { value: NAME_LF, label: 'Full name, last name first', group: "Person's full name, split into first and last name" },
    ...fieldOptions.map((t) => ({ value: t.lead_column, label: t.ui_label, group: 'Merchant fields' })),
  ], [fieldOptions]);
  const used = useMemo(() => {
    const m = new Map<string, number[]>();
    Object.entries(choice).forEach(([pos, t]) => { if (isField(t)) m.set(t, [...(m.get(t) ?? []), Number(pos)]); });
    return m;
  }, [choice]);
  const setColumn = (pos: number, value: string) => {
    setChoice((c) => ({ ...c, [pos]: value }));
    setTouched((t) => new Set(t).add(pos));
    setPhoneOrder((order) => value === PHONE ? (order.includes(pos) ? order : [...order, pos]) : order.filter((p) => p !== pos));
    setOrigin((o) => (o === 'ai' ? 'ai' : 'manual'));
  };
  const movePhone = (pos: number, dir: -1 | 1) => setPhoneOrder((order) => {
    const i = order.indexOf(pos); const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return order;
    const n = [...order]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const save = useMutation({
    mutationFn: () => confirmMapping({
      batch: batch.id, sheet: sheet.id, profile, profileName: profileName.trim() || null, origin,
      rules: (s?.headers ?? []).map((h) => ruleOf(h.position, choice[h.position] ?? IGNORE)),
      phonePriority: phoneOrder, applySameLayout: true, suggestion: origin === 'ai' ? aiInfo?.id ?? null : null,
    }),
    onSuccess: (r) => {
      toast.success(r.sheets_pinned > 1 ? `Saved for ${sheet.sheet_name} and ${r.sheets_pinned - 1} other sheet${r.sheets_pinned > 2 ? 's' : ''} with the same columns.` : `Saved for ${sheet.sheet_name}.`);
      queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch.id) });
      onSaved();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (suggestion.isLoading) return <p className="li-muted">Reading the columns…</p>;
  if (suggestion.error || !s) return <p className="li-inline-error">{(suggestion.error as Error)?.message ?? 'Could not read the columns.'}</p>;
  const header = (pos: number) => s.headers.find((h) => h.position === pos)?.header || `Column ${pos + 1}`;
  const label = (v: string) => v === IGNORE ? 'Ignore' : v === EXTRA ? 'Keep as extra data' : v === PHONE ? 'Phone number'
    : v === NAME_FL ? 'Full name, first name first' : v === NAME_LF ? 'Full name, last name first'
    : fieldOptions.find((t) => t.lead_column === v)?.ui_label ?? v;
  const samples = s.sample_rows ?? [];
  const value = (pos: number) => choice[pos] ?? IGNORE;
  const matched = s.headers.filter((h) => value(h.position) !== IGNORE).length;
  // An AI mark holds only while the column still has the value the AI chose.
  const byAi = (pos: number) => !!aiInfo?.notes[pos]?.changed && aiInfo.notes[pos].value === value(pos);
  const aiCount = s.headers.filter((h) => byAi(h.position)).length;
  const view = show === 'ai' && aiCount === 0 ? 'all' : show;
  const shown = s.headers.filter((h) => view === 'all' || (view === 'ai' ? byAi(h.position) : value(h.position) === IGNORE));
  const splitExample = (pos: number) => {
    const ex = s.evidence?.find((e) => e.position === pos)?.examples.find((x) => (value(pos) === NAME_LF ? x.last_first : x.first_last).some(Boolean));
    if (!ex) return null;
    const [f, l] = value(pos) === NAME_LF ? ex.last_first : ex.first_last;
    return `"${ex.value}" becomes ${f ?? '(none)'} / ${l ?? '(none)'}`;
  };

  return (
    <div className="li-mapper">
      <div className="li-mapper__bar">
        <div className="li-mapper__source">
          {profile && s.exact_profile && profile === s.exact_profile.profile && origin === 'profile' && (
            <span title={s.exact_profile.name}>Using the saved mapping for this layout.</span>
          )}
          {aiCount > 0 && aiInfo && <span>AI ({aiInfo.model}) changed {aiCount} column{aiCount === 1 ? '' : 's'} after reading the headers and the first values. They are marked AI.</span>}
          {origin === 'alias' && !s.exact_profile && <span>Matched by column name and the first values. Check before saving.</span>}
        </div>
        <div className="li-mapper__tools">
          <button type="button" className="li-btn li-btn--secondary" disabled={ai.isPending} onClick={() => ai.mutate()}>
            {ai.isPending ? 'Asking AI…' : 'Suggest with AI'}
          </button>
        </div>
      </div>

      <NamePanel s={s} value={value} header={header} label={label} aiNotes={aiInfo?.notes ?? {}} onUse={setColumn} />

      <div className="li-phones">
        <h4>Phones, in the order tried</h4>
        {phoneOrder.length === 0 ? (
          <p className="li-inline-error">Mark at least one column as a phone number.</p>
        ) : (
          <ol>
            {phoneOrder.map((pos, i) => (
              <li key={pos}>
                <span className="li-phones__rank">{i === 0 ? 'Main' : `Backup ${i}`}</span>
                <b>{header(pos)}</b>
                <button type="button" className="li-btn li-btn--icon" disabled={i === 0} onClick={() => movePhone(pos, -1)} aria-label={`Move ${header(pos)} up`}>↑</button>
                <button type="button" className="li-btn li-btn--icon" disabled={i === phoneOrder.length - 1} onClick={() => movePhone(pos, 1)} aria-label={`Move ${header(pos)} down`}>↓</button>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="li-mapper__filter">
        <span><b>{matched}</b> of {s.headers.length} columns used</span>
        <div className="li-seg li-seg--sm" role="group" aria-label="Which columns to show">
          <button type="button" className={`li-seg__opt${view === 'all' ? ' is-on' : ''}`} aria-pressed={view === 'all'} onClick={() => setShow('all')}>All</button>
          <button type="button" className={`li-seg__opt${view === 'ignored' ? ' is-on' : ''}`} aria-pressed={view === 'ignored'} onClick={() => setShow('ignored')}>Ignored ({s.headers.length - matched})</button>
          {aiCount > 0 && <button type="button" className={`li-seg__opt${view === 'ai' ? ' is-on' : ''}`} aria-pressed={view === 'ai'} onClick={() => setShow('ai')}>Changed by AI ({aiCount})</button>}
        </div>
      </div>

      <div className="li-maptable" role="table" aria-label={`Columns of ${sheet.sheet_name}`}>
        <div className="li-maptable__head" role="row">
          <span role="columnheader">Column in the file</span>
          <span role="columnheader">First values</span>
          <span role="columnheader">CRM field</span>
        </div>
        {shown.map((h) => {
          const v = value(h.position);
          const dup = isField(v) && (used.get(v)?.length ?? 0) > 1;
          const ai = byAi(h.position) ? aiInfo!.notes[h.position] : null;
          const rank = phoneOrder.indexOf(h.position);
          const split = isName(v) ? splitExample(h.position) : null;
          return (
            <div key={h.position} role="row" className={`li-maptable__row${v === PHONE ? ' is-phone' : v === IGNORE || v === EXTRA ? ' is-keep' : ' is-mapped'}${ai ? ' is-ai' : ''}`}>
              <span role="cell" className="li-maptable__col">
                <span className="li-maptable__letter">{colLetter(h.position)}</span>
                <span className="li-maptable__header">{h.header || <i className="li-muted">no header</i>}</span>
                {ai && <span className="li-aitag" title="Chosen by AI. Check it before saving.">AI</span>}
              </span>
              <span role="cell" className="li-maptable__samples">
                {samples.slice(0, 3).map((r, i) => <span key={i} className="li-sample" title={r.cells[h.position]}>{r.cells[h.position] || <i>empty</i>}</span>)}
              </span>
              <span role="cell" className="li-maptable__target">
                <PickList value={v} onChange={(next) => setColumn(h.position, next)} fill filterable options={targetOptions}
                  ariaLabel={`CRM field for ${h.header || `column ${h.position + 1}`}`} />
                {v === PHONE && <span className="li-maptable__hint">{rank === 0 ? 'Main phone' : `Backup phone ${rank}`}</span>}
                {split && <span className="li-maptable__hint">{split}</span>}
                {dup && <span className="li-maptable__hint">Also used by another column. The first non-empty value wins; others are kept.</span>}
                {ai && <span className="li-maptable__hint li-maptable__hint--ai">{ai.reason ?? 'Chosen by AI.'}{ai.confidence != null ? ` (${Math.round(ai.confidence * 100)}% sure)` : ''}</span>}
              </span>
            </div>
          );
        })}
      </div>


      <footer className="li-panel__foot li-panel__foot--split">
        <label className="li-field li-field--inline">
          <span className="li-field__label">Mapping name</span>
          <InputGroup value={profileName} placeholder={`${batch.source ?? 'Lead file'}, ${sheet.sheet_name}`} onChange={(e) => setProfileName(e.target.value)} />
        </label>
        <button type="button" className="li-btn li-btn--primary" disabled={phoneOrder.length === 0 || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : `Save columns for ${sheet.sheet_name}`}
        </button>
      </footer>
    </div>
  );
}

// Where the lead's first and last name come from, what the first rows give,
// and any column where the rule-based checks or the AI disagree with the
// current choice, with a one-click fix. The import itself applies the same
// rules in lead_import_collapse: an explicit First Name or Last Name column
// wins over a split full name.
function NamePanel({ s, value, header, label, aiNotes, onUse }: {
  s: MappingSuggestion; value: (pos: number) => string; header: (pos: number) => string; label: (v: string) => string;
  aiNotes: Record<number, AiNote>; onUse: (pos: number, value: string) => void;
}) {
  const positions = s.headers.map((h) => h.position);
  const firstCol = positions.find((p) => value(p) === 'first_name');
  const lastCol = positions.find((p) => value(p) === 'last_name');
  const splitCol = positions.find((p) => isName(value(p)));
  const nameRelated = (v: string | undefined) => v === 'first_name' || v === 'last_name' || isName(v);
  const split = (pos: number, raw: string | undefined) => {
    const v = (raw ?? '').trim().replace(/\s+/g, ' ');
    const ex = s.evidence?.find((e) => e.position === pos)?.examples.find((x) => x.value === v);
    return ex ? (value(pos) === NAME_LF ? ex.last_first : ex.first_last) : null;
  };
  const rows = (s.sample_rows ?? []).slice(0, 3).map((r) => {
    const parts = splitCol != null ? split(splitCol, r.cells[splitCol]) : null;
    return {
      source: [firstCol, lastCol, splitCol].filter((p, i, all) => p != null && all.indexOf(p) === i).map((p) => r.cells[p!] ?? '').filter(Boolean).join(', '),
      first: firstCol != null ? r.cells[firstCol] || null : parts?.[0] ?? null,
      last: lastCol != null ? r.cells[lastCol] || null : parts?.[1] ?? null,
    };
  }).filter((r) => r.source);
  const fixes = positions.flatMap((p) => {
    const ai = aiNotes[p];
    const rule = s.suggested_rules.find((r) => r.position === p);
    const ruleValue = rule ? choiceOf(rule) : undefined;
    const offer = ai && ai.value !== value(p) && (nameRelated(ai.value) || nameRelated(value(p)))
      ? { by: 'AI', to: ai.value, reason: ai.reason }
      : ruleValue && ruleValue !== value(p) && (nameRelated(ruleValue) || nameRelated(value(p)))
        ? { by: 'Check', to: ruleValue, reason: rule?.reason ?? null }
        : null;
    return offer ? [{ pos: p, ...offer }] : [];
  });
  const from = (field: number | undefined, part: string) => field != null ? <>from <b>{header(field)}</b></>
    : splitCol != null ? <>{part} of <b>{header(splitCol)}</b></> : <span className="li-inline-error">no column</span>;
  return (
    <div className="li-names" role="region" aria-label="Lead name">
      <h4>Lead name</h4>
      <dl className="li-dl li-names__from">
        <dt>First name</dt><dd>{from(firstCol, 'first word(s)')}</dd>
        <dt>Last name</dt><dd>{from(lastCol, 'last word(s)')}</dd>
      </dl>
      {rows.length > 0 && (
        <table className="li-mini li-names__rows">
          <thead><tr><th>In the file</th><th>First name</th><th>Last name</th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i}><td>{r.source}</td><td>{r.first ?? <i className="li-muted">empty</i>}</td><td>{r.last ?? <i className="li-muted">empty</i>}</td></tr>
          ))}</tbody>
        </table>
      )}
      {fixes.length > 0 && (
        <ul className="li-names__fixes">
          {fixes.map((f) => (
            <li key={f.pos}>
              <span className={f.by === 'AI' ? 'li-aitag' : 'li-pilltag'}>{f.by}</span>
              <span><b>{header(f.pos)}</b> should be {label(f.to)}. {f.reason}</span>
              <button type="button" className="li-btn li-btn--link" onClick={() => onUse(f.pos, f.to)}>Use {label(f.to)}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Step 4: review and import ────────────────────────── */

function ReviewStep({ batch, done, goTo, onOpenBatch }: {
  batch: BatchDetail; done: Record<StepId, boolean>; goTo: (s: StepId) => void; onOpenBatch: (id: number) => void;
}) {
  const queryClient = useQueryClient();
  const [openPart, setOpenPart] = useState<LedgerPart | null>(null);
  const [confirming, setConfirming] = useState(false);
  const cost = costOf(batch);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch.id) });
  const prepare = useMutation({
    mutationFn: () => requestPrepare(batch.id),
    onSuccess: (r) => { if (!r.queued) toast.error(r.blocking?.[0]?.message ?? 'Finish the earlier steps first.'); invalidate(); },
    onError: (e) => toast.error((e as Error).message),
  });
  const retry = useMutation({ mutationFn: () => retryStage(batch.id), onSuccess: invalidate, onError: (e) => toast.error((e as Error).message) });
  const accept = useMutation({
    mutationFn: ({ verify, recheck }: { verify: VerifyKind[]; recheck: boolean }) => acceptBatch(batch.id, verify, recheck),
    onSuccess: (r) => {
      setConfirming(false);
      if (!r.accepted) toast.error(r.message ?? 'This exact file was already imported.');
      else toast.success('Import started. You can leave this page; it finishes on the server.');
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const missing = (['file', 'sheets', 'columns'] as StepId[]).filter((s) => !done[s]);
  const blocked = prepare.data && !prepare.data.queued ? prepare.data.blocking?.[0]?.message ?? 'Finish the earlier steps first.' : null;
  // The preview builds on its own once the setup is complete: once per visit
  // to this step, so a refusal is shown rather than retried in a loop.
  const autoStarted = useRef(false);
  const needsPreview = !missing.length && batch.status === 'Draft' && !batch.stage_error;
  useEffect(() => {
    if (!needsPreview || autoStarted.current) return;
    autoStarted.current = true;
    prepare.mutate();
  }, [needsPreview, prepare]);
  if (missing.length) {
    return (
      <section className="li-panel">
        <header className="li-panel__head"><h2>Almost there</h2><p>Finish these first:</p></header>
        <ul className="li-todo">
          {missing.map((m) => <li key={m}><button type="button" className="li-btn li-btn--link" onClick={() => goTo(m)}>{STEPS.find((s) => s.id === m)?.label}</button></li>)}
        </ul>
      </section>
    );
  }
  if (batch.status === 'Preparing' || (batch.status === 'Draft' && batch.stage_error?.stage === 'prepare')) {
    return (
      <section className="li-panel">
        <header className="li-panel__head"><h2>Building the preview</h2><p>The server applies your column choices to every row and checks each phone against earlier imports and merchants. Nothing is imported yet.</p></header>
        <JobBanner job={batch.job} stageError={batch.stage_error} onRetry={() => retry.mutate()} retrying={retry.isPending} />
      </section>
    );
  }
  const p: Preview | null = batch.status === 'Previewed' ? batch.preview : null;
  if (!p) {
    return (
      <section className="li-panel">
        <header className="li-panel__head"><h2>Building the preview</h2><p>It shows how many leads this file gives, how many are already known, and a sample, before anything is imported.</p></header>
        {blocked || prepare.isError ? (
          <div className="li-banner li-banner--warn" role="alert">
            <div className="li-banner__text"><b>The preview did not start.</b> {blocked ?? (prepare.error as Error).message}</div>
            <button type="button" className="li-btn li-btn--secondary" disabled={prepare.isPending} onClick={() => prepare.mutate()}>Try again</button>
          </div>
        ) : (
          <div className="li-banner li-banner--progress" aria-live="polite"><Spinner size={16} /><div className="li-banner__text">Starting.</div></div>
        )}
      </section>
    );
  }
  const c = p.counts;
  const skipped = c.skipped_missing_phone + c.skipped_invalid_phone;
  const duplicate = p.blocking.find((b) => b.code === 'duplicate_delivery');

  return (
    <section className="li-panel li-panel--wide">
      <header className="li-panel__head">
        <h2>{fmtInt(c.observations)} leads ready to import</h2>
        <p className="li-filerow">
          <FileChip batchId={batch.id} name={batch.file?.original_filename ?? batch.file_name} bytes={batch.file?.byte_size} />
          <span>{batch.source}{batch.sub_lead_source ? ` (${batch.sub_lead_source})` : ''}, received {fmtDay(batch.batch_date)}</span>
        </p>
      </header>
      <RowLedger rows={c.delivered_rows} leads={c.observations} merged={c.collapsed_rows} skipped={skipped} onOpen={setOpenPart} />
      <p className="li-muted li-ledger__hint">Click a count to see those rows.</p>
      {openPart === 'rows' && <FilePreviewDialog batchId={batch.id} name={batch.file?.original_filename ?? batch.file_name ?? 'Uploaded file'} bytes={batch.file?.byte_size} onClose={() => setOpenPart(null)} />}
      {openPart && openPart !== 'rows' && <PreviewRowsDialog batch={batch} kind={openPart} onClose={() => setOpenPart(null)} />}

      <div className="li-review">
        <div className="li-review__col">
          <h4>Already known</h4>
          <dl className="li-dl">
            <dt>Received in an earlier import</dt><dd>{fmtInt(p.known.earlier_lead)}</dd>
            <dt>Phone belongs to a merchant</dt><dd>{fmtInt(p.known.existing_merchant)}</dd>
            <dt>Never seen before</dt><dd><b>{fmtInt(p.known.new_identity)}</b></dd>
          </dl>
          <p className="li-muted">Repeats are still imported as leads of this batch, so every delivery stays on record.</p>
        </div>
        <div className="li-review__col">
          <h4>Can be assigned to reps</h4>
          <dl className="li-dl">
            <dt>Not assignable, already a merchant</dt><dd>{fmtInt(p.suppression.not_assignable)}</dd>
            <dt>of which had deals</dt><dd>{fmtInt(p.suppression.deal_history)}</dd>
            <dt>of which do not disturb</dt><dd>{fmtInt(p.suppression.dnd)}</dd>
            <dt>Phone matches several merchants</dt><dd>{fmtInt(p.link_estimate.competing_merchants)}</dd>
          </dl>
          {p.link_estimate.competing_merchants > 0 && <p className="li-muted">Those go to the hold queue for review.</p>}
        </div>
        {((p.per_sheet ?? []).length > 1 || cost > 0) && (
          <div className="li-review__col">
            {(p.per_sheet ?? []).length > 1 && <>
              <h4>By sheet</h4>
              <table className="li-mini">
                <thead><tr><th>Sheet</th><th className="r">Rows</th><th className="r">Usable</th><th className="r">Skipped</th></tr></thead>
                <tbody>{p.per_sheet.map((s) => <tr key={s.id}><td>{s.sheet_name}</td><td className="r">{fmtInt(s.rows)}</td><td className="r">{fmtInt(s.usable)}</td><td className="r">{fmtInt(s.skipped)}</td></tr>)}</tbody>
              </table>
            </>}
            {cost > 0 && <p className="li-muted">Cost {fmtMoney(cost)}: {fmtMoney(p.counts.observations ? cost / p.counts.observations : null)} per lead, {fmtMoney(p.known.new_identity ? cost / p.known.new_identity : null)} per new one.</p>}
          </div>
        )}
      </div>

      {p.extra_columns.length > 0 && (
        <div className="li-kept">
          <span className="li-muted">Kept as extra data</span>
          <div className="li-chips">{p.extra_columns.map((c) => <span key={c} className="li-chip li-chip--static">{c}</span>)}</div>
        </div>
      )}
      {p.ignored_columns.length > 0 && (
        <div className="li-kept">
          <span className="li-muted">Ignored, not imported</span>
          <div className="li-chips">{p.ignored_columns.map((c) => <span key={c} className="li-chip li-chip--static">{c}</span>)}</div>
        </div>
      )}

      {p.blocking.length > 0 && (
        <div className="li-banner li-banner--danger" role="alert">
          <div className="li-banner__text">
            <b>This cannot be imported yet.</b>
            <ul>{p.blocking.map((b, i) => <li key={i}>{b.message}</li>)}</ul>
            {duplicate?.existing_batch && <button type="button" className="li-btn li-btn--link" onClick={() => onOpenBatch(duplicate.existing_batch!)}>Open the earlier import</button>}
          </div>
        </div>
      )}
      <footer className="li-panel__foot">
        <button type="button" className="li-btn li-btn--quiet" disabled={prepare.isPending} onClick={() => prepare.mutate()}>Rebuild preview</button>
        <button type="button" className="li-btn li-btn--primary li-btn--large" disabled={!p.can_accept || accept.isPending} onClick={() => setConfirming(true)}>
          {accept.isPending ? 'Starting…' : `Import ${fmtInt(c.observations)} leads`}
        </button>
      </footer>
      {confirming && <ImportConfirm batch={batch} leads={c.observations} pending={accept.isPending}
        onImport={(verify, recheck) => accept.mutate({ verify, recheck })} onClose={() => setConfirming(false)} />}
      <p className="li-muted li-review__fine">Importing claims this delivery: the same file with the same source and batch date cannot be imported twice.</p>
    </section>
  );
}
